import { meFetcher } from '../data/User';
import { apiClient } from '../data/apiClient';
import { appConfig } from '../data/AppConfig';
import { beginLogout } from '../utils/authRedirect';
import { track, resetIdentity } from '../utils/mixpanel';
import { AnalyticsEvents } from '../analytics/events';
import { AxiosError } from 'axios';
import useSWR from 'swr';

const useAuth = () => {
    const {
        data: meData,
        isLoading,
        error,
        mutate,
    } = useSWR<Awaited<ReturnType<typeof meFetcher>>, AxiosError>(
        { entity: 'user/me' },
        meFetcher,
        { revalidateOnFocus: true },
    );

    const isUnauthorized = error?.response?.status === 401;

    const logout = async () => {
        // Emit before reset() clears the identity, so the event is still
        // attributed to the departing user.
        track(AnalyticsEvents.LoggedOut);
        resetIdentity();
        beginLogout();
        try {
            await apiClient.get(`${appConfig.REACT_APP_BACKEND_URL}/api/logout`);
        } catch {
            // Session may already be expired; still clear client state and redirect.
        }
        await mutate(undefined, { revalidate: false });
        window.location.assign(`${appConfig.REACT_APP_FRONTEND_URL}/sign-in`);
    };

    return {
        user: isUnauthorized ? null : meData?.user ?? null,
        organization: isUnauthorized ? null : meData?.organization ?? null,
        activeOrg: isUnauthorized ? null : meData?.activeOrg ?? null,
        isStaff: isUnauthorized ? false : meData?.isStaff ?? false,
        isSuperAdmin: isUnauthorized ? false : meData?.isSuperAdmin ?? false,
        permissions: isUnauthorized ? [] : meData?.permissions ?? [],
        isLoading,
        error,
        isUnauthorized,
        logout,
        mutate,
    };
};

export { useAuth };
